import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DashboardIcon from '@mui/icons-material/Dashboard'
import EventNoteIcon from '@mui/icons-material/EventNote'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import PeopleIcon from '@mui/icons-material/People'
import PollIcon from '@mui/icons-material/Poll'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
	BackLink,
	Backdrop,
	Brand,
	GroupLabel,
	NavLink,
	Sidebar,
	Spacer,
} from './AdminSidebar.styles'

interface NavItem {
	to: string
	label: string
	icon: React.ReactNode
	/** Extra path that should also light this item up (the index route). */
	alsoMatches?: string
}

/**
 * Two groups because the split is real: the first aggregates a whole cohort, the
 * rest browse individual records. New analytics views join the first group rather
 * than lengthening one undifferentiated list.
 */
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
	{
		label: 'Analytics',
		items: [
			{ to: '/admin/overview', label: 'Overview', icon: <DashboardIcon />, alsoMatches: '/admin' },
			{ to: '/admin/onboarding', label: 'Onboarding', icon: <PollIcon /> },
			{ to: '/admin/funnel', label: 'Funnel', icon: <FilterAltIcon /> },
		],
	},
	{
		label: 'Records',
		items: [
			{ to: '/admin/users', label: 'Users', icon: <PeopleIcon /> },
			{ to: '/admin/trips', label: 'Trips', icon: <FlightTakeoffIcon /> },
			{ to: '/admin/events', label: 'Events', icon: <EventNoteIcon /> },
			{ to: '/admin/ideas', label: 'Ideas', icon: <LightbulbIcon /> },
			{ to: '/admin/expenses', label: 'Expenses', icon: <ReceiptLongIcon /> },
			{ to: '/admin/itinerary-imports', label: 'Imports', icon: <UploadFileIcon /> },
		],
	},
]

const isActive = (pathname: string, item: NavItem): boolean =>
	pathname.startsWith(item.to) || (item.alsoMatches !== undefined && pathname === item.alsoMatches)

interface AdminSidebarProps {
	/** Only meaningful below the breakpoint; above it the column is always shown. */
	open: boolean
	onClose: () => void
}

/**
 * The admin surface's primary navigation: a permanent column on desktop, and a
 * dismissible drawer on narrow screens.
 *
 * As a drawer it closes on every exit a user might reach for — Escape, the
 * backdrop, and navigating — because it covers the page it just navigated to.
 * Focus moves into the drawer when it opens and returns to whatever opened it on
 * close, so keyboard users are never stranded behind the overlay.
 */
const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose }) => {
	const { pathname } = useLocation()
	const sidebarRef = useRef<HTMLElement>(null)
	const wasOpen = useRef(false)

	// Navigating dismisses the drawer: it sits over the content on narrow screens,
	// so leaving it open would hide the page the user just asked for.
	useEffect(() => {
		onClose()
		// Only a route change should trigger this, not a new onClose identity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname])

	useEffect(() => {
		if (!open) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [open, onClose])

	// Send focus into the drawer on open, and back to the trigger on close, so the
	// overlay never leaves keyboard focus on content hidden behind it.
	useEffect(() => {
		if (open) {
			wasOpen.current = true
			sidebarRef.current?.querySelector('a')?.focus()
			return
		}
		if (wasOpen.current) {
			wasOpen.current = false
			const trigger = document.querySelector<HTMLElement>('[aria-controls="admin-nav"]')
			if (sidebarRef.current?.contains(document.activeElement)) trigger?.focus()
		}
	}, [open])

	return (
		<>
			{open && <Backdrop data-testid="admin-backdrop" onClick={onClose} />}

			<Sidebar id="admin-nav" ref={sidebarRef} $open={open} aria-label="Admin">
				<Brand>Orbit Admin</Brand>

				{NAV_GROUPS.map(group => (
					<React.Fragment key={group.label}>
						<GroupLabel>{group.label}</GroupLabel>
						{group.items.map(item => {
							const active = isActive(pathname, item)
							return (
								<NavLink
									key={item.to}
									to={item.to}
									$active={active}
									aria-current={active ? 'page' : undefined}
								>
									{item.icon}
									{item.label}
								</NavLink>
							)
						})}
					</React.Fragment>
				))}

				<Spacer />
				<BackLink to="/home">
					<ArrowBackIcon />
					Back to app
				</BackLink>
			</Sidebar>
		</>
	)
}

export default AdminSidebar
